import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { RatioCardSkeleton } from "@/components/skeletons";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  Percent,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Minus,
  Info,
} from "lucide-react";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { exportRatiosPDF, exportRatiosExcel } from "@/utils/exportUtils";
import { FileText, FileSpreadsheet, Download } from "lucide-react";

export default function Ratios() {
  return (
    <DashboardLayout>
      <RatiosContent />
    </DashboardLayout>
  );
}

function RatiosContent() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(1);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("2024");
  const [selectedQuarter, setSelectedQuarter] = useState<string>("Q4");

  // Fetch companies
  const { data: companies = [] } = trpc.company.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Calculate date range
  const year = parseInt(selectedPeriod);
  const quarterMap: Record<string, { start: number; end: number }> = {
    Q1: { start: 0, end: 2 },
    Q2: { start: 3, end: 5 },
    Q3: { start: 6, end: 8 },
    Q4: { start: 9, end: 11 },
  };
  const quarter = quarterMap[selectedQuarter];
  const startDate = new Date(year, quarter.start, 1).getTime();
  const endDate = new Date(year, quarter.end + 1, 0, 23, 59, 59).getTime();

  // Fetch ratios
  const { data: ratios, isLoading } = trpc.statement.ratios.useQuery(
    {
      companyId: selectedCompanyId,
      startDate,
      endDate,
    },
    {
      enabled: isAuthenticated && selectedCompanyId > 0,
    }
  );

  const getRatioStatus = (value: number, benchmark: any, inverse = false) => {
    if (!benchmark) return "neutral";

    if (inverse) {
      // For ratios where lower is better (e.g., debt ratios)
      if (value <= benchmark.target) return "good";
      if (value <= benchmark.max) return "warning";
      return "poor";
    } else {
      // For ratios where higher is better
      if (value >= benchmark.target) return "good";
      if (value >= benchmark.min) return "warning";
      return "poor";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "good":
        return "text-[var(--success)] bg-[var(--success)]/10 border-[var(--success)]/20";
      case "warning":
        return "text-[var(--warning)] bg-[var(--warning)]/10 border-[var(--warning)]/20";
      case "poor":
        return "text-destructive bg-destructive/10 border-destructive/20";
      default:
        return "text-muted-foreground bg-muted/20 border-muted";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "good":
        return <ArrowUp className="h-4 w-4" />;
      case "warning":
        return <Minus className="h-4 w-4" />;
      case "poor":
        return <ArrowDown className="h-4 w-4" />;
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  const formatRatio = (value: number, decimals = 2) => {
    return value.toFixed(decimals);
  };

  const formatPercent = (value: number, decimals = 1) => {
    return `${value.toFixed(decimals)}%`;
  };

  const getProgressValue = (value: number, benchmark: any) => {
    if (!benchmark) return 50;
    const range = benchmark.max - benchmark.min;
    const position = ((value - benchmark.min) / range) * 100;
    return Math.max(0, Math.min(100, position));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = getLoginUrl();
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Financial Ratios Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Key performance metrics with Swiss SME industry benchmarks
          </p>
        </div>
        {ratios && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                exportRatiosPDF(ratios, `Company ${selectedCompanyId}`);
                toast.success("Financial Ratios exported to PDF");
              }}
            >
              <FileText className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                exportRatiosExcel(ratios, `Company ${selectedCompanyId}`);
                toast.success("Financial Ratios exported to Excel");
              }}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        )}
      </div>

      {/* Period Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Analysis Parameters</CardTitle>
          <CardDescription>Select company and reporting period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Company</Label>
              <Select
                value={selectedCompanyId.toString()}
                onValueChange={value => setSelectedCompanyId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.length === 0 ? (
                    <SelectItem value="1">Default Company</SelectItem>
                  ) : (
                    companies.map(company => (
                      <SelectItem
                        key={company.id}
                        value={company.id.toString()}
                      >
                        {company.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fiscal Year</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2022">2022</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quarter</Label>
              <Select
                value={selectedQuarter}
                onValueChange={setSelectedQuarter}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1">Q1 (Jan-Mar)</SelectItem>
                  <SelectItem value="Q2">Q2 (Apr-Jun)</SelectItem>
                  <SelectItem value="Q3">Q3 (Jul-Sep)</SelectItem>
                  <SelectItem value="Q4">Q4 (Oct-Dec)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-primary/20 rounded animate-pulse" />
            <div className="h-7 w-36 bg-muted rounded animate-pulse" />
          </div>
          <RatioCardSkeleton count={3} />
          <div className="flex items-center gap-2 mt-6">
            <div className="h-5 w-5 bg-primary/20 rounded animate-pulse" />
            <div className="h-7 w-40 bg-muted rounded animate-pulse" />
          </div>
          <RatioCardSkeleton count={3} />
        </div>
      ) : ratios ? (
        <>
          {/* Liquidity Ratios */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Liquidity Ratios</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Current Ratio */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.liquidity.currentRatio.value, ratios.liquidity.currentRatio.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Current Ratio</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Current Assets / Current Liabilities. Measures ability
                          to pay short-term obligations.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(ratios.liquidity.currentRatio.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.liquidity.currentRatio.value,
                        ratios.liquidity.currentRatio.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.liquidity.currentRatio.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatRatio(
                          ratios.liquidity.currentRatio.benchmark.min
                        )}
                        -
                        {formatRatio(
                          ratios.liquidity.currentRatio.benchmark.max
                        )}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.liquidity.currentRatio.value,
                        ratios.liquidity.currentRatio.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Quick Ratio */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.liquidity.quickRatio.value, ratios.liquidity.quickRatio.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Quick Ratio</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          (Cash + Receivables) / Current Liabilities. Acid test
                          of liquidity.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(ratios.liquidity.quickRatio.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.liquidity.quickRatio.value,
                        ratios.liquidity.quickRatio.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.liquidity.quickRatio.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatRatio(ratios.liquidity.quickRatio.benchmark.min)}
                        -
                        {formatRatio(ratios.liquidity.quickRatio.benchmark.max)}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.liquidity.quickRatio.value,
                        ratios.liquidity.quickRatio.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Cash Ratio */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.liquidity.cashRatio.value, ratios.liquidity.cashRatio.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Cash Ratio</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Cash / Current Liabilities. Most conservative
                          liquidity measure.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(ratios.liquidity.cashRatio.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.liquidity.cashRatio.value,
                        ratios.liquidity.cashRatio.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.liquidity.cashRatio.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatRatio(ratios.liquidity.cashRatio.benchmark.min)}-
                        {formatRatio(ratios.liquidity.cashRatio.benchmark.max)}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.liquidity.cashRatio.value,
                        ratios.liquidity.cashRatio.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Leverage Ratios */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Leverage Ratios</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Debt-to-Equity */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.leverage.debtToEquity.value, ratios.leverage.debtToEquity.benchmark, true))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Debt-to-Equity</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Total Debt / Total Equity. Measures financial
                          leverage.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(ratios.leverage.debtToEquity.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.leverage.debtToEquity.value,
                        ratios.leverage.debtToEquity.benchmark,
                        true
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.leverage.debtToEquity.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Max:{" "}
                        {formatRatio(
                          ratios.leverage.debtToEquity.benchmark.max
                        )}
                      </span>
                    </div>
                    <Progress
                      value={
                        100 -
                        getProgressValue(
                          ratios.leverage.debtToEquity.value,
                          ratios.leverage.debtToEquity.benchmark
                        )
                      }
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Debt Ratio */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.leverage.debtRatio.value, ratios.leverage.debtRatio.benchmark, true))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Debt Ratio</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Total Liabilities / Total Assets. Percentage of assets
                          financed by debt.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatPercent(ratios.leverage.debtRatio.value * 100)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.leverage.debtRatio.value,
                        ratios.leverage.debtRatio.benchmark,
                        true
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatPercent(
                          ratios.leverage.debtRatio.benchmark.target * 100
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Max:{" "}
                        {formatPercent(
                          ratios.leverage.debtRatio.benchmark.max * 100
                        )}
                      </span>
                    </div>
                    <Progress
                      value={
                        100 -
                        getProgressValue(
                          ratios.leverage.debtRatio.value,
                          ratios.leverage.debtRatio.benchmark
                        )
                      }
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Equity Multiplier */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.leverage.equityMultiplier.value, ratios.leverage.equityMultiplier.benchmark, true))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Equity Multiplier
                    </CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Total Assets / Total Equity. Measures financial
                          leverage from equity perspective.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(ratios.leverage.equityMultiplier.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.leverage.equityMultiplier.value,
                        ratios.leverage.equityMultiplier.benchmark,
                        true
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.leverage.equityMultiplier.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Max:{" "}
                        {formatRatio(
                          ratios.leverage.equityMultiplier.benchmark.max
                        )}
                      </span>
                    </div>
                    <Progress
                      value={
                        100 -
                        getProgressValue(
                          ratios.leverage.equityMultiplier.value,
                          ratios.leverage.equityMultiplier.benchmark
                        )
                      }
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Profitability Ratios */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Profitability Ratios</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Gross Margin */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.profitability.grossMargin.value, ratios.profitability.grossMargin.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Gross Margin</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Gross Profit / Revenue. Profitability after direct
                          costs.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatPercent(ratios.profitability.grossMargin.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.profitability.grossMargin.value,
                        ratios.profitability.grossMargin.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatPercent(
                          ratios.profitability.grossMargin.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatPercent(
                          ratios.profitability.grossMargin.benchmark.min
                        )}
                        -
                        {formatPercent(
                          ratios.profitability.grossMargin.benchmark.max
                        )}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.profitability.grossMargin.value,
                        ratios.profitability.grossMargin.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Operating Margin */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.profitability.operatingMargin.value, ratios.profitability.operatingMargin.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Operating Margin
                    </CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Operating Profit / Revenue. Profitability from
                          operations.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatPercent(
                        ratios.profitability.operatingMargin.value
                      )}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.profitability.operatingMargin.value,
                        ratios.profitability.operatingMargin.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatPercent(
                          ratios.profitability.operatingMargin.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatPercent(
                          ratios.profitability.operatingMargin.benchmark.min
                        )}
                        -
                        {formatPercent(
                          ratios.profitability.operatingMargin.benchmark.max
                        )}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.profitability.operatingMargin.value,
                        ratios.profitability.operatingMargin.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Net Margin */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.profitability.netMargin.value, ratios.profitability.netMargin.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Net Margin</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Net Profit / Revenue. Bottom-line profitability.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatPercent(ratios.profitability.netMargin.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.profitability.netMargin.value,
                        ratios.profitability.netMargin.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatPercent(
                          ratios.profitability.netMargin.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatPercent(
                          ratios.profitability.netMargin.benchmark.min
                        )}
                        -
                        {formatPercent(
                          ratios.profitability.netMargin.benchmark.max
                        )}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.profitability.netMargin.value,
                        ratios.profitability.netMargin.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* ROE */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.profitability.roe.value, ratios.profitability.roe.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Return on Equity (ROE)
                    </CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Net Profit / Total Equity (annualized). Return to
                          shareholders.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatPercent(ratios.profitability.roe.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.profitability.roe.value,
                        ratios.profitability.roe.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatPercent(
                          ratios.profitability.roe.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatPercent(ratios.profitability.roe.benchmark.min)}-
                        {formatPercent(ratios.profitability.roe.benchmark.max)}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.profitability.roe.value,
                        ratios.profitability.roe.benchmark
                      )}
                      className="h-2"
                    />
                    {ratios.period.annualized && (
                      <Badge variant="outline" className="text-xs">
                        Annualized
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* ROA */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.profitability.roa.value, ratios.profitability.roa.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Return on Assets (ROA)
                    </CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Net Profit / Total Assets (annualized). Asset
                          efficiency.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatPercent(ratios.profitability.roa.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.profitability.roa.value,
                        ratios.profitability.roa.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatPercent(
                          ratios.profitability.roa.benchmark.target
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatPercent(ratios.profitability.roa.benchmark.min)}-
                        {formatPercent(ratios.profitability.roa.benchmark.max)}
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.profitability.roa.value,
                        ratios.profitability.roa.benchmark
                      )}
                      className="h-2"
                    />
                    {ratios.period.annualized && (
                      <Badge variant="outline" className="text-xs">
                        Annualized
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Efficiency Ratios */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Efficiency Ratios</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Asset Turnover */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.efficiency.assetTurnover.value, ratios.efficiency.assetTurnover.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Asset Turnover</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Revenue / Total Assets (annualized). How efficiently
                          assets generate revenue.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(ratios.efficiency.assetTurnover.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.efficiency.assetTurnover.value,
                        ratios.efficiency.assetTurnover.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.efficiency.assetTurnover.benchmark.target
                        )}
                        x
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatRatio(
                          ratios.efficiency.assetTurnover.benchmark.min
                        )}
                        -
                        {formatRatio(
                          ratios.efficiency.assetTurnover.benchmark.max
                        )}
                        x
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.efficiency.assetTurnover.value,
                        ratios.efficiency.assetTurnover.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Receivables Turnover */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.efficiency.receivablesTurnover.value, ratios.efficiency.receivablesTurnover.benchmark))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Receivables Turnover
                    </CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Revenue / Receivables (annualized). How quickly
                          receivables are collected.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(ratios.efficiency.receivablesTurnover.value)}
                    </span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.efficiency.receivablesTurnover.value,
                        ratios.efficiency.receivablesTurnover.benchmark
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.efficiency.receivablesTurnover.benchmark.target
                        )}
                        x
                      </span>
                      <span className="text-muted-foreground">
                        Range:{" "}
                        {formatRatio(
                          ratios.efficiency.receivablesTurnover.benchmark.min
                        )}
                        -
                        {formatRatio(
                          ratios.efficiency.receivablesTurnover.benchmark.max
                        )}
                        x
                      </span>
                    </div>
                    <Progress
                      value={getProgressValue(
                        ratios.efficiency.receivablesTurnover.value,
                        ratios.efficiency.receivablesTurnover.benchmark
                      )}
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Days Receivables Outstanding */}
              <Card
                className={`border-2 ${getStatusColor(getRatioStatus(ratios.efficiency.daysReceivablesOutstanding.value, ratios.efficiency.daysReceivablesOutstanding.benchmark, true))}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Days Receivables Outstanding
                    </CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          365 / Receivables Turnover. Average collection period
                          in days.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {formatRatio(
                        ratios.efficiency.daysReceivablesOutstanding.value,
                        0
                      )}
                    </span>
                    <span className="text-sm text-muted-foreground">days</span>
                    {getStatusIcon(
                      getRatioStatus(
                        ratios.efficiency.daysReceivablesOutstanding.value,
                        ratios.efficiency.daysReceivablesOutstanding.benchmark,
                        true
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Target:{" "}
                        {formatRatio(
                          ratios.efficiency.daysReceivablesOutstanding.benchmark
                            .target,
                          0
                        )}{" "}
                        days
                      </span>
                      <span className="text-muted-foreground">
                        Max:{" "}
                        {formatRatio(
                          ratios.efficiency.daysReceivablesOutstanding.benchmark
                            .max,
                          0
                        )}{" "}
                        days
                      </span>
                    </div>
                    <Progress
                      value={
                        100 -
                        getProgressValue(
                          ratios.efficiency.daysReceivablesOutstanding.value,
                          ratios.efficiency.daysReceivablesOutstanding.benchmark
                        )
                      }
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No data available for the selected period</p>
              <p className="text-sm mt-2">
                Please ensure transactions exist for this company and period
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
